"use client";
import React from "react";
import {
  Box,
  Container,
  Grid,
  Typography,
  IconButton,
  Divider,
  TextField,
  Button,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { motion } from "framer-motion";
import {
  Facebook,
  Twitter,
  LinkedIn,
  Instagram,
  WhatsApp,
  LocationOn,
  Phone,
  Email,
  AccessTime,
  Send,
  ArrowUpward,
  Copyright,
} from "@mui/icons-material";
import { useLanguage } from "./LanguageProvider";
import { useNavigate, useLocation } from "react-router-dom";

export const Footer = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { currentLang, isInitialized } = useLanguage();
  const isRTL = currentLang === "ar";
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isHomePage = location.pathname === "/";

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const yOffset = -80;
      const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;
      window.scrollTo({ top: y, behavior: "smooth" });
    }
  };

  const handleNavigation = (link, sectionId) => {
    // إذا كان الرابط لصفحة منفصلة
    if (link === "/about" || link === "/services" || link === "/blogs" || link === "/contact") {
      navigate(link);
    } 
    // إذا كان الرابط لقسم في الصفحة الرئيسية
    else if (sectionId) {
      // إذا كنا مش في الصفحة الرئيسية، نروح لها الأول
      if (!isHomePage) {
        navigate("/");
        setTimeout(() => {
          scrollToSection(sectionId);
        }, 100);
      } else {
        scrollToSection(sectionId);
      }
    }
  };

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        type: "spring",
        stiffness: 100,
        damping: 15,
      },
    },
  };

  // Translations
  const translations = {
    en: {
      companyName: "RUKN ALMUROOJ",
      companyDesc: "Administrative and legal document clearing services in Dubai and UAE. We provide integrated solutions with accuracy and professionalism.",
      quickLinks: "Quick Links",
      ourServices: "Our Services",
      contactUs: "Contact Us",
      address: "Park Lane Tower, Office 1901, Business Bay, Dubai",
      phone: "+971 50 333 0841",
      email: "info@ruknalmurooj.com",
      workingHours: "Sun - Thu: 9AM - 6PM",
      newsletter: "Subscribe to Newsletter",
      newsletterPlaceholder: "Your email address",
      copyright: "RUKN ALMUROOJ Documents Clearing Services. All rights reserved.",
      privacyPolicy: "Privacy Policy",
      termsConditions: "Terms & Conditions",
      sitemap: "Sitemap",
      servicesList: [
        { name: "Rental Dispute Center Follow-up", link: "/services", sectionId: "services" },
        { name: "Government Transactions", link: "/services", sectionId: "services" },
        { name: "Form & Request Filling", link: "/services", sectionId: "services" },
        { name: "Document Preparation", link: "/services", sectionId: "services" },
        { name: "Archiving & Organization", link: "/services", sectionId: "services" },
      ],
      quickLinksList: [
        { name: "Home", link: "/", sectionId: "home" },
        { name: "About Us", link: "/about", sectionId: null },
        { name: "Our Services", link: "/services", sectionId: null },
        { name: "Blog", link: "/blogs", sectionId: null },
        { name: "Contact Us", link: "/contact", sectionId: null },
      ],
    },
    ar: {
      companyName: "ركن المروج",
      companyDesc: "خدمات تصفية المعاملات الإدارية والقانونية في دبي والإمارات. نقدم حلولاً متكاملة بدقة واحترافية.",
      quickLinks: "روابط سريعة",
      ourServices: "خدماتنا",
      contactUs: "تواصل معنا",
      address: "برج بارك لين، مكتب 1901، بيزنس باي، دبي",
      phone: "+971 50 333 0841",
      email: "info@ruknalmurooj.com",
      workingHours: "الأحد - الخميس: 9ص - 6م",
      newsletter: "اشترك في النشرة البريدية",
      newsletterPlaceholder: "بريدك الإلكتروني",
      copyright: "ركن المروج لخدمات تصفية المعاملات. جميع الحقوق محفوظة.",
      privacyPolicy: "سياسة الخصوصية",
      termsConditions: "الشروط والأحكام",
      sitemap: "خريطة الموقع",
      servicesList: [
        { name: "متابعة طلبات مركز فض المنازعات", link: "/services", sectionId: "services" },
        { name: "المعاملات الحكومية", link: "/services", sectionId: "services" },
        { name: "تعبئة النماذج والطلبات", link: "/services", sectionId: "services" },
        { name: "تجهيز المستندات", link: "/services", sectionId: "services" },
        { name: "أرشفة وتنظيم المستندات", link: "/services", sectionId: "services" },
      ],
      quickLinksList: [
        { name: "الرئيسية", link: "/", sectionId: "home" },
        { name: "من نحن", link: "/about", sectionId: null },
        { name: "خدماتنا", link: "/services", sectionId: null },
        { name: "المدونة", link: "/blogs", sectionId: null },
        { name: "اتصل بنا", link: "/contact", sectionId: null },
      ],
    },
  };

  const t = translations[currentLang];

  const socialMedia = [
    { icon: Facebook, link: "https://facebook.com", color: "#1877F2", name: "Facebook" },
    { icon: Twitter, link: "https://twitter.com", color: "#1DA1F2", name: "Twitter" },
    { icon: LinkedIn, link: "https://linkedin.com", color: "#0A66C2", name: "LinkedIn" },
    { icon: Instagram, link: "https://instagram.com", color: "#E4405F", name: "Instagram" },
    { icon: WhatsApp, link: "https://wa.me/971503330841", color: "#25D366", name: "WhatsApp" },
  ];

  const handleNewsletter = (e) => {
    e.preventDefault();
    const message = currentLang === "ar" 
      ? "تم الاشتراك في النشرة البريدية بنجاح!" 
      : "Successfully subscribed to newsletter!";
    alert(message);
  };

  if (!isInitialized) {
    return null;
  }

  return (
    <Box
      component="footer"
      sx={{
        position: "relative",
        background: "linear-gradient(135deg, #f8f9fa 0%, #ffffff 50%, #f5f5f5 100%)",
        color: "#1a1a2e",
        pt: { xs: 6, md: 8 },
        pb: { xs: 3, md: 4 },
        overflow: "hidden",
        borderTop: "1px solid rgba(0,0,0,0.05)",
      }}
    >
      {/* Animated Background */}
      <motion.div
        animate={{
          scale: [1, 1.2, 1],
          rotate: [0, 45, 0],
          opacity: [0.03, 0.06, 0.03],
        }}
        transition={{
          duration: 20,
          repeat: Infinity,
          ease: "linear",
        }}
        style={{
          position: "absolute",
          top: "10%",
          right: "-10%",
          width: "400px",
          height: "400px",
          borderRadius: "50%",
          background: "radial-gradient(circle, rgba(212,175,55,0.2) 0%, rgba(212,175,55,0) 70%)",
          zIndex: 0,
          pointerEvents: "none",
        }}
      />

      <motion.div
        animate={{
          y: [0, -20, 0],
        }}
        transition={{
          duration: 8,
          repeat: Infinity,
          ease: "easeInOut",
        }}
        style={{
          position: "absolute",
          bottom: "5%",
          left: "-5%",
          width: "250px",
          height: "250px",
          borderRadius: "50%",
          background: "radial-gradient(circle, rgba(212,175,55,0.15) 0%, rgba(212,175,55,0) 70%)",
          zIndex: 0,
          pointerEvents: "none",
        }}
      />

      <Container maxWidth="xl" sx={{ position: "relative", zIndex: 1 }}>
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.1 }}
        >
          <Grid container spacing={4}>
            {/* Column 1 - Logo & About */}
            <Grid item xs={12} sm={6} md={3}>
              <motion.div variants={itemVariants}>
                <Box 
                  sx={{ mb: 3, cursor: "pointer" }}
                  onClick={() => handleNavigation("/", "home")}
                >
                  <Typography
                    variant="h5"
                    sx={{
                      fontWeight: "bold",
                      background: "linear-gradient(135deg, #c9a03d, #a07d2e)",
                      backgroundClip: "text",
                      WebkitBackgroundClip: "text",
                      color: "transparent",
                      mb: 2,
                    }}
                  >
                    {t.companyName}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: "#666",
                      lineHeight: 1.8,
                      fontSize: "0.85rem",
                    }}
                  >
                    {t.companyDesc}
                  </Typography>
                </Box>

                {/* Social Media */}
                <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", justifyContent: isRTL ? "flex-end" : "flex-start" }}>
                  {socialMedia.map((social, index) => {
                    const Icon = social.icon;
                    return (
                      <motion.div
                        key={index}
                        whileHover={{ scale: 1.1, y: -3 }}
                        whileTap={{ scale: 0.95 }}
                      >
                        <IconButton
                          component="a"
                          href={social.link}
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{
                            bgcolor: "rgba(0,0,0,0.05)",
                            color: social.color,
                            width: 38,
                            height: 38,
                            "&:hover": {
                              bgcolor: social.color,
                              color: "white",
                            },
                          }}
                        >
                          <Icon sx={{ fontSize: 20 }} />
                        </IconButton>
                      </motion.div>
                    );
                  })}
                </Box>
              </motion.div>
            </Grid>

            {/* Column 2 - Quick Links */}
            <Grid item xs={12} sm={6} md={3}>
              <motion.div variants={itemVariants}>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: "bold",
                    color: "#c9a03d",
                    mb: 3,
                    fontSize: "1.1rem",
                    position: "relative",
                    display: "inline-block",
                    "&::after": {
                      content: '""',
                      position: "absolute",
                      bottom: -8,
                      [isRTL ? "right" : "left"]: 0,
                      width: 40,
                      height: 3,
                      background: "#c9a03d",
                      borderRadius: "2px",
                    },
                  }}
                >
                  {t.quickLinks}
                </Typography>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, alignItems: isRTL ? "flex-end" : "flex-start" }}>
                  {t.quickLinksList.map((link, index) => (
                    <motion.div key={index} whileHover={{ x: isRTL ? -5 : 5 }}>
                      <Typography
                        onClick={() => handleNavigation(link.link, link.sectionId)}
                        sx={{
                          color: "#555",
                          textDecoration: "none",
                          fontSize: "0.9rem",
                          transition: "all 0.3s ease",
                          display: "inline-block",
                          cursor: "pointer",
                          "&:hover": {
                            color: "#c9a03d",
                          },
                        }}
                      >
                        {link.name}
                      </Typography>
                    </motion.div>
                  ))}
                </Box>
              </motion.div>
            </Grid>

            {/* Column 3 - Services */}
            <Grid item xs={12} sm={6} md={3}>
              <motion.div variants={itemVariants}>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: "bold",
                    color: "#c9a03d",
                    mb: 3,
                    fontSize: "1.1rem",
                    position: "relative",
                    display: "inline-block",
                    "&::after": {
                      content: '""',
                      position: "absolute",
                      bottom: -8,
                      [isRTL ? "right" : "left"]: 0,
                      width: 40,
                      height: 3,
                      background: "#c9a03d",
                      borderRadius: "2px",
                    },
                  }}
                >
                  {t.ourServices}
                </Typography>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, alignItems: isRTL ? "flex-end" : "flex-start" }}>
                  {t.servicesList.map((service, index) => (
                    <motion.div key={index} whileHover={{ x: isRTL ? -5 : 5 }}>
                      <Typography
                        onClick={() => handleNavigation(service.link, service.sectionId)}
                        sx={{
                          color: "#555",
                          textDecoration: "none",
                          fontSize: "0.85rem",
                          transition: "all 0.3s ease",
                          display: "block",
                          cursor: "pointer",
                          "&:hover": {
                            color: "#c9a03d",
                          },
                        }}
                      >
                        • {service.name}
                      </Typography>
                    </motion.div>
                  ))}
                </Box>
              </motion.div>
            </Grid>

            {/* Column 4 - Contact & Newsletter */}
            <Grid item xs={12} sm={6} md={3}>
              <motion.div variants={itemVariants}>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: "bold",
                    color: "#c9a03d",
                    mb: 3,
                    fontSize: "1.1rem",
                    position: "relative",
                    display: "inline-block",
                    "&::after": {
                      content: '""',
                      position: "absolute",
                      bottom: -8,
                      [isRTL ? "right" : "left"]: 0,
                      width: 40,
                      height: 3,
                      background: "#c9a03d",
                      borderRadius: "2px",
                    },
                  }}
                >
                  {t.contactUs}
                </Typography>

                <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mb: 3, alignItems: isRTL ? "flex-end" : "flex-start" }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexDirection: isRTL ? "row-reverse" : "row" }}>
                    <LocationOn sx={{ color: "#c9a03d", fontSize: 18 }} />
                    <Typography variant="body2" sx={{ color: "#555", fontSize: "0.8rem", textAlign: isRTL ? "right" : "left" }}>
                      {t.address}
                    </Typography>
                  </Box>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexDirection: isRTL ? "row-reverse" : "row" }}>
                    <Phone sx={{ color: "#c9a03d", fontSize: 18 }} />
                    <Typography variant="body2" sx={{ color: "#555", fontSize: "0.8rem" }}>
                      {t.phone}
                    </Typography>
                  </Box>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexDirection: isRTL ? "row-reverse" : "row" }}>
                    <Email sx={{ color: "#c9a03d", fontSize: 18 }} />
                    <Typography variant="body2" sx={{ color: "#555", fontSize: "0.8rem" }}>
                      {t.email}
                    </Typography>
                  </Box>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexDirection: isRTL ? "row-reverse" : "row" }}>
                    <AccessTime sx={{ color: "#c9a03d", fontSize: 18 }} />
                    <Typography variant="body2" sx={{ color: "#555", fontSize: "0.8rem" }}>
                      {t.workingHours}
                    </Typography>
                  </Box>
                </Box>

                {/* Newsletter */}
                <Typography
                  variant="body2"
                  sx={{
                    color: "#1a1a2e",
                    mb: 1.5,
                    fontSize: "0.85rem",
                    fontWeight: 500,
                    textAlign: isRTL ? "right" : "left",
                  }}
                >
                  {t.newsletter}
                </Typography>
                <form onSubmit={handleNewsletter} style={{ display: "flex", gap: "8px", flexDirection: isRTL ? "row-reverse" : "row" }}>
                  <TextField
                    size="small"
                    placeholder={t.newsletterPlaceholder}
                    variant="outlined"
                    dir={isRTL ? "rtl" : "ltr"}
                    sx={{
                      flex: 1,
                      "& .MuiOutlinedInput-root": {
                        color: "#333",
                        borderRadius: "30px",
                        backgroundColor: "#fff",
                        "& fieldset": {
                          borderColor: "rgba(0,0,0,0.2)",
                        },
                        "&:hover fieldset": {
                          borderColor: "#c9a03d",
                        },
                        "&.Mui-focused fieldset": {
                          borderColor: "#c9a03d",
                        },
                      },
                      "& .MuiInputBase-input": {
                        fontSize: "0.8rem",
                      },
                    }}
                  />
                  <Button
                    type="submit"
                    variant="contained"
                    sx={{
                      minWidth: "auto",
                      bgcolor: "#c9a03d",
                      borderRadius: "30px",
                      px: 2,
                      "&:hover": {
                        bgcolor: "#a07d2e",
                      },
                    }}
                  >
                    <Send sx={{ fontSize: 18 }} />
                  </Button>
                </form>
              </motion.div>
            </Grid>
          </Grid>

          {/* Divider */}
          <Divider
            sx={{
              my: 4,
              borderColor: "rgba(0,0,0,0.08)",
            }}
          />

          {/* Bottom Bar */}
          <Grid container alignItems="center" justifyContent="space-between" spacing={2}>
            <Grid item xs={12} md={6}>
              <Typography
                variant="body2"
                sx={{
                  color: "#888",
                  textAlign: { xs: "center", md: isRTL ? "right" : "left" },
                  fontSize: "0.75rem",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: { xs: "center", md: isRTL ? "flex-end" : "flex-start" },
                  gap: 0.5,
                }}
              >
                <Copyright sx={{ fontSize: 14 }} />
                {new Date().getFullYear()} {t.copyright}
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: { xs: "center", md: "flex-end" },
                  gap: 3,
                  flexDirection: isRTL ? "row-reverse" : "row",
                }}
              >
                <Typography
                  onClick={() => handleNavigation("/", "home")}
                  sx={{
                    color: "#888",
                    textDecoration: "none",
                    fontSize: "0.7rem",
                    cursor: "pointer",
                    "&:hover": { color: "#c9a03d" },
                  }}
                >
                  {t.privacyPolicy}
                </Typography>
                <Typography
                  onClick={() => handleNavigation("/", "home")}
                  sx={{
                    color: "#888",
                    textDecoration: "none",
                    fontSize: "0.7rem",
                    cursor: "pointer",
                    "&:hover": { color: "#c9a03d" },
                  }}
                >
                  {t.termsConditions}
                </Typography>
                <Typography
                  onClick={() => handleNavigation("/", "home")}
                  sx={{
                    color: "#888",
                    textDecoration: "none",
                    fontSize: "0.7rem",
                    cursor: "pointer",
                    "&:hover": { color: "#c9a03d" },
                  }}
                >
                  {t.sitemap}
                </Typography>
              </Box>
            </Grid>
          </Grid>

          {/* Scroll to Top Button */}
          <motion.div
            initial={{ opacity: 0, scale: 0 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 1 }}
            style={{
              position: "fixed",
              bottom: 30,
              right: 30,
              zIndex: 1000,
            }}
          >
            <IconButton
              onClick={scrollToTop}
              sx={{
                bgcolor: "#c9a03d",
                color: "#fff",
                width: 45,
                height: 45,
                "&:hover": {
                  bgcolor: "#a07d2e",
                  transform: "translateY(-3px)",
                },
                transition: "all 0.3s ease",
              }}
            >
              <ArrowUpward />
            </IconButton>
          </motion.div>
        </motion.div>
      </Container>
    </Box>
  );
};

export default Footer;